/* eslint-disable arrow-body-style */
import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useFormikContext } from "formik";
import { toast } from "react-toastify";
import { Dialog } from "@mui/material";
import Tooltip from "../../../components/shared/Tooltip";
import {
  VStack,
  HStack,
  ButtonPrimary1,
  ButtonPrimary,
  ButtonCancelSecondary,
  Error,
  ButtonSecondary,
  Box,
} from "../../../components/utils";
import { Input, Label, TextArea } from "../../../components/shared/InputField";
import { Select } from "../../../components/shared/Select";
import { SwitchButton } from "../../../components/shared/SwitchButton";
import {
  TransactionDetailsData,
  UploadedFile,
} from "../../../types/Transaction";
import {
  getPresignedUrlForKey,
  validateDocumentTemplate,
  previewDocumentTemplate,
} from "../../../api/Esop";
import { downloadS3File } from "../../../utils/DownloadFile";
import { MultiSelectV2 } from "../../../components/shared/MultiSelectV2";
import { useGetCompanyPaymentProfiles } from "../../../queries/payment";
import { useUserDetailsV2 } from "../../../queries";
import { MonetaryEventState } from "../../../types/OptionBuyback";
import { saveTransactionDetails } from "../../../api/TransactionApisV2";
import TemplateVariablesModal from "./TemplateVariablesModal";

interface ExerciseDetailsProps {
  onStepChange: (e?: number) => void;
  onBackClick: () => void;
}

const ExerciseDetails = (props: ExerciseDetailsProps) => {
  const {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    validateForm,
    setTouched,
  } = useFormikContext<TransactionDetailsData>();

  const [inputDisabled, setInputDisabled] = useState(false);

  useEffect(() => {
    if (!values.eventState || values.eventState === MonetaryEventState.DRAFT) {
      setInputDisabled(false);
    } else {
      setInputDisabled(true);
    }
  }, [values.eventState]);

  const {
    sendTransactionEmails,
    transactionPaymentEnabled,
    autoApproveExerciseRequests,
    generateExerciseLetters,
    isDematAccountMandatory,
    allowedDematDepositories,
    hissaGeneratedPaymentEnabled: isPaymentLinkRequired,
    hissaGeneratedTaxPaymentEnabled: isTaxPaymentLinkRequired,
    paymentLink,
    taxPaymentLink,
    defaultTemplateLink,
    defaultEmailTemplates,
    taxMandatoryForCompletion,
    paymentData,
    strictlyConsiderCompanyCurrency,
    taxDetailsInfoText,
    hideTaxDetails,
  } = values.monetaryDetails;
  const { data: paymentProfiles } = useGetCompanyPaymentProfiles();
  const { data: userDetails } = useUserDetailsV2();
  const isInternalUser =
    userDetails?.emailId?.endsWith("@rulezero.com") ?? false;
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState<string>("");
  const [validationResults, setValidationResults] = useState<
    { name: string; valid: boolean; reason: string | null }[]
  >([]);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [showVariablesModal, setShowVariablesModal] = useState(false);

  const handleSubmit = () => {
    if (values.monetaryDetails?.considerBlackOutDays) {
      props.onStepChange(3);
      return;
    }
    props.onStepChange(4);
    if (!values.id) {
      saveTransactionDetails(values);
    }
  };

  async function handleFileUpload(event: React.ChangeEvent<HTMLInputElement>) {
    // Validate files and show results popup BEFORE the if block
    const validFileNames = new Set<string>();
    const validFiles: File[] = [];

    if (event.target.files) {
      const files = Array.from(event.target.files);
      const docxFilesToValidate = files.filter(
        (file) =>
          file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      );

      if (docxFilesToValidate.length > 0) {
        const results = await Promise.all(
          docxFilesToValidate.map(async (file) => {
            const result = await validateDocumentTemplate(
              file,
              "EXERCISE_LETTER"
            )
              .then((res) => res)
              .catch((err) => {
                const errMsg = err.response.data?.errorRaw;
                return {
                  data: {
                    valid: false,
                    errorRaw: errMsg || "Validation error",
                  },
                };
              });
            const valid = result?.data?.valid ?? false;
            if (valid) validFiles.push(file);
            return {
              valid,
              name: file.name,
              reason: result?.data?.errorRaw || "Validation failed",
            };
          })
        );
        setValidationResults(results);
        if (results.some((res) => !res.valid)) {
          setShowValidationDialog(true);
          toast.error(
            "Please resolve the validation errors before uploading the files."
          );
          return;
        }
      }
    }

    if (validFiles.length > 0) {
      const newFiles = Array.from(validFiles);

      // Filter files to allow only .docx files
      const docxFiles = newFiles.filter(
        (file) =>
          file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      );

      if (docxFiles.length === 0) {
        toast.error("Please upload a valid .docx file.");
        return;
      }

      // Convert files to UploadedFile format (only valid files)
      const uploadedFiles = docxFiles.map(async (file) => {
        const base64 = (await fileToBase64(file)).split(",")[1];
        return { name: file.name, type: file.type, size: file.size, base64 };
      });

      Promise.all(uploadedFiles).then((files) => {
        if (files.length === 0) return;
        const updatedTemplates = [
          ...(values.monetaryDetails.customTemplates || []),
          ...files,
        ];
        setFieldValue("monetaryDetails.customTemplates", updatedTemplates);
        const fileInput = document.getElementById(
          "fileInput"
        ) as HTMLInputElement | null;
        if (fileInput) {
          fileInput.value = "";
        }
      });
    }
  }

  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  const handleToggleEmail = (template: any) => {
    const updatedTemplates = defaultEmailTemplates?.map((t) =>
      t.templateType === template.templateType
        ? { ...t, sendEmail: !t.sendEmail }
        : t
    );
    setFieldValue("monetaryDetails.defaultEmailTemplates", updatedTemplates);
  };

  const handlePreview = (url: string) => {
    getPresignedUrlForKey(url)
      .then((res) => {
        const s3Url = res.data;
        return fetch(s3Url);
      })
      .then((response) => {
        return response.text();
      })
      .then((htmlContent) => {
        setPreviewContent(htmlContent);
        setShowPreview(true);
      });
  };

  const createDownloadUrl = (base64String: string) => {
    let base64Content = base64String;
    if (base64String.startsWith("data:application/octet-stream;base64,")) {
      base64Content = base64String.split(",")[1];
    }
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length)
      .fill(0)
      .map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/octet-stream" });
    return URL.createObjectURL(blob);
  };

  const handlePreviewCustomTemplate = async (file: UploadedFile) => {
    let base64Content = file.base64;
    if (file.base64.includes(",")) {
      base64Content = file.base64.split(",")[1];
    }
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length)
      .fill(0)
      .map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
    const fileObj = new File([blob], file.name, {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
    const responseBlob = await previewDocumentTemplate(
      fileObj,
      "EXERCISE_LETTER"
    );
    const url = URL.createObjectURL(responseBlob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `preview_${file.name}`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const handleDefaultTemplate = (url: string, e: any) => {
    if (url) {
      getPresignedUrlForKey(url).then((res) => {
        downloadS3File(res.data, "DefaultTemplate.docx", false);
      });
    }
    e.preventDefault();
  };

  const [selectedAdminEmails, setSelectedAdminEmails] = useState<boolean>(true);

  return (
    <VStack className="w-full">
      <VStack className="justify-between gap-1 pb-3 pl-6 font-medium border-b-[0.5px] pt-7">
        <HStack className="flex justify-between font-semibold text-lg1 text-black-501 ">
          Exercise - Basic Details
        </HStack>
        <HStack className="flex font-medium text-sm3 text-gray-401">
          Configure payment preferences, employee email triggers, and approval
          flows — everything you need to manage how this exercise event runs.
        </HStack>
      </VStack>
      <VStack className="justify-between pt-8 pb-2 pl-6 text-base font-medium border-b ">
        <HStack className="justify-between">
          <div className="flex justify-between ">
            Exercise Confirmation Letters
          </div>
          <div
            className={`justify-end pr-6 ${
              inputDisabled ? "cursor-not-allowed" : ""
            }`}
          >
            <SwitchButton
              onClick={() => {
                if (inputDisabled) return;
                setFieldValue(
                  "monetaryDetails.generateExerciseLetters",
                  !generateExerciseLetters
                );
              }}
              className=""
              value={generateExerciseLetters}
              label=""
              disabled={inputDisabled}
            />
          </div>
        </HStack>
        <p className="font-medium text-gray-400 text-xxs">
          Enabling this would show Exercise letter to employees
        </p>
      </VStack>
      {generateExerciseLetters && (
        <VStack className="gap-4 pt-4">
          {!values.monetaryDetails.customTemplates ||
          values.monetaryDetails.customTemplates.length === 0 ? (
            <HStack>
              <a
                className="flex-1 px-6 text-sm font-normal text-blue-600 cursor-pointer hover:underline"
                href={
                  values.monetaryDetails.defaultTemplateLink
                    ? `/download/${values.monetaryDetails.defaultTemplateLink}`
                    : "#"
                }
                onClick={(e) => {
                  const s3Key = values.monetaryDetails.defaultTemplateLink;
                  if (!s3Key) {
                    e.preventDefault();
                    return;
                  }
                  handleDefaultTemplate(s3Key, e);
                }}
              >
                Default Template
              </a>
              <div className="flex-1"></div>
            </HStack>
          ) : (
            <HStack>
              <VStack className="gap-2 px-6 max-h-40">
                {values.monetaryDetails.customTemplates?.map((file, index) => {
                  const downloadUrl = createDownloadUrl(file.base64);
                  return (
                    <HStack
                      key={index}
                      className="items-center justify-between gap-2 text-sm font-normal"
                    >
                      <a
                        href={downloadUrl}
                        download={file.name}
                        className="text-blue-500 hover:underline"
                      >
                        {file.name}
                      </a>
                      {isInternalUser && (
                        <Tooltip
                          text="Download preview docx - renders your template with sample data so you can see how the final document will look"
                          className="!bottom-auto !mb-0 top-full mt-2"
                          _className="w-[400px]"
                        >
                          <div>
                            <button
                              onClick={() => handlePreviewCustomTemplate(file)}
                              className="text-blue-500 hover:text-blue-700"
                            >
                              <Icon
                                icon="mdi:file-download"
                                width={20}
                                height={20}
                              />
                            </button>
                          </div>
                        </Tooltip>
                      )}
                      <button
                        onClick={() => {
                          const updatedTemplates =
                            values.monetaryDetails.customTemplates?.filter(
                              (_, i) => i !== index
                            );
                          setFieldValue(
                            "monetaryDetails.customTemplates",
                            updatedTemplates
                          );
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Icon icon="mdi:delete" width={20} height={20} />
                      </button>
                    </HStack>
                  );
                })}
              </VStack>
              <div className="flex-1"></div>
            </HStack>
          )}
          <HStack className="justify-end gap-2">
            <HStack className="flex-shrink-0 gap-2 px-6">
              <ButtonPrimary1
                className={`px-4 py-2 ${
                  inputDisabled ? "cursor-not-allowed" : ""
                }`}
                onClick={() => {
                  const fileInput = document.getElementById(
                    "fileInput"
                  ) as HTMLInputElement | null;
                  if (fileInput) {
                    fileInput.value = "";
                    fileInput.click();
                  }
                }}
                disabled={inputDisabled}
              >
                {!values.monetaryDetails.customTemplates ||
                values.monetaryDetails.customTemplates.length === 0
                  ? "Add Custom Template"
                  : "Add Another Custom Document"}
              </ButtonPrimary1>
              {isInternalUser && (
                <ButtonPrimary1
                  className="px-4 py-2"
                  onClick={() => setShowVariablesModal(true)}
                >
                  <HStack className="items-center gap-1">
                    <Icon
                      icon="material-symbols:menu-book-outline"
                      width={16}
                    />
                    <span>View Template Variables</span>
                  </HStack>
                </ButtonPrimary1>
              )}
              {values.monetaryDetails.customTemplates &&
                values.monetaryDetails.customTemplates.length !== 0 && (
                  <ButtonSecondary
                    className={`px-4 py-2 ${
                      inputDisabled ? "cursor-not-allowed" : ""
                    }`}
                    disabled={inputDisabled}
                    onClick={() => {
                      if (inputDisabled) return;
                      const fileInput = document.getElementById(
                        "fileInput"
                      ) as HTMLInputElement | null;
                      if (fileInput) {
                        fileInput.value = "";
                      }
                      setFieldValue("monetaryDetails.customTemplates", []);
                    }}
                  >
                    Use Default Template
                  </ButtonSecondary>
                )}
            </HStack>
            <input
              id="fileInput"
              type="file"
              multiple
              className="hidden"
              onChange={handleFileUpload}
            />
          </HStack>
          <div className="justify-end gap-2 px-6">
            <HStack className="justify-between gap-2 ">
              <Label className="mb-0 text-sm font-normal">
                Enable Signature Flow for Employee
              </Label>
              <div className="justify-end">
                <SwitchButton
                  onClick={() =>
                    setFieldValue(
                      "monetaryDetails.employeeSignatureRequired",
                      !values.monetaryDetails.employeeSignatureRequired
                    )
                  }
                  disabled={inputDisabled}
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  onBlur={handleBlur}
                  value={values.monetaryDetails.employeeSignatureRequired}
                  label=""
                />
              </div>
            </HStack>
            <p className="font-medium text-gray-400 text-xxs">
              Enabling this would ask for employees to confirm the exercise
              letter by signing on it.
            </p>
          </div>
          {values.monetaryDetails.employeeSignatureRequired && (
            <VStack className="pl-5">
              <div className="justify-end gap-2 px-6">
                <HStack className="justify-between gap-2 ">
                  <Label className="mb-0 text-sm font-normal">
                    Enable E-Sign for Exercise Letter
                  </Label>
                  <div className="justify-end">
                    <SwitchButton
                      onClick={() =>
                        setFieldValue(
                          "monetaryDetails.enableESignForExerciseLetter",
                          !values.monetaryDetails.enableESignForExerciseLetter
                        )
                      }
                      disabled={inputDisabled}
                      className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                      onBlur={handleBlur}
                      value={
                        values.monetaryDetails.enableESignForExerciseLetter
                      }
                      label=""
                    />
                  </div>
                </HStack>
                <p className="font-medium text-gray-400 text-xxs">
                  Enabling this would enable esign for exercise letter
                </p>
              </div>
              {values.monetaryDetails.enableESignForExerciseLetter && (
                <div className="justify-end gap-2 px-6 pl-10">
                  <HStack
                    className={`gap-40 ${
                      inputDisabled ? "cursor-not-allowed" : ""
                    }`}
                  >
                    <HStack className="items-center gap-2" radioGroup="">
                      <input
                        type="radio"
                        name="signatureOption"
                        checked={
                          values.monetaryDetails.eSignMethod === "digio_aadhar"
                        }
                        disabled={inputDisabled}
                        className={`accent-orange-501 outline-hidden mr-1 cursor-pointer ${
                          inputDisabled ? "cursor-not-allowed" : ""
                        }`}
                        onChange={(e) => {
                          setFieldValue(
                            "monetaryDetails.eSignMethod",
                            "digio_aadhar"
                          );
                        }}
                      />
                      <Label className="mt-2">Use Aadhar for Signature</Label>
                    </HStack>
                    <HStack
                      className={`items-center gap-2 ${
                        inputDisabled ? "cursor-not-allowed" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="signatureOption"
                        checked={
                          values.monetaryDetails.eSignMethod ===
                          "digio_electronic"
                        }
                        disabled={inputDisabled}
                        className={`accent-orange-501 outline-hidden mr-1 cursor-pointer ${
                          inputDisabled ? "cursor-not-allowed" : ""
                        }`}
                        onBlur={handleBlur}
                        onChange={(e) => {
                          setFieldValue(
                            "monetaryDetails.eSignMethod",
                            "digio_electronic"
                          );
                        }}
                      />
                      <Label className="mt-2">Use Electronic pad</Label>
                    </HStack>
                  </HStack>
                </div>
              )}
            </VStack>
          )}
        </VStack>
      )}
      <HStack className="justify-between pt-8 pb-2 text-base font-medium border-b ">
        <div className="flex justify-between pl-6">Payment Link</div>
        <div className="justify-end pr-6">
          <SwitchButton
            onClick={() => {
              setFieldValue(
                "monetaryDetails.transactionPaymentEnabled",
                !transactionPaymentEnabled
              );
            }}
            disabled={inputDisabled}
            className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
            value={transactionPaymentEnabled}
            label=""
          />
        </div>
      </HStack>
      {transactionPaymentEnabled && (
        <div>
          <HStack className="gap-8 px-6 py-2">
            <div className="flex-1 pt-4">
              <HStack className="justify-between gap-2 ">
                <Label className="mb-0 text-sm font-normal">
                  Enable Payment Link From Hissa
                </Label>
                <div className="justify-end">
                  <SwitchButton
                    onClick={() => {
                      if (inputDisabled) return;
                      setFieldValue(
                        "monetaryDetails.hissaGeneratedPaymentEnabled",
                        !isPaymentLinkRequired
                      );
                    }}
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    value={isPaymentLinkRequired || false}
                    label=""
                  />
                </div>
              </HStack>
              <p className="font-medium text-gray-400 text-xxs">
                Enabling this would generate a payment link for the employee
                through Juspay
              </p>
            </div>
            {!isPaymentLinkRequired ? (
              <div className="flex-1">
                <Label className="mb-0 text-sm font-normal">Payment Link</Label>
                <Input
                  type="text"
                  disabled={inputDisabled}
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  name="monetaryDetails.paymentLink"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder="Enter Payment Link"
                  value={paymentLink}
                />
                {touched.monetaryDetails?.paymentLink &&
                  errors.monetaryDetails?.paymentLink && (
                    <Error text={errors.monetaryDetails.paymentLink}></Error>
                  )}
              </div>
            ) : (
              <div className="flex-1">
                <Label className="mb-0 text-sm font-normal">
                  Payment Profile
                </Label>
                <Select
                  onChange={(evt) => {
                    if (inputDisabled) return;
                    setFieldValue(
                      "monetaryDetails.paymentData.paymentIdentifier",
                      evt.target.value
                    );
                  }}
                  disabled={inputDisabled}
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  value={
                    values?.monetaryDetails?.paymentData?.paymentIdentifier
                  }
                  options={paymentProfiles || []}
                  textGetter={(e) => e.name}
                  valueGetter={(e) => e.id}
                />
              </div>
            )}
          </HStack>
          <HStack className="gap-8 px-6 py-2">
            <div className="flex-1 pt-4">
              <HStack className="justify-between gap-2 ">
                <Label className="mb-0 text-sm font-normal">
                  Enable Tax Payment link
                </Label>
                <div className="justify-end">
                  <SwitchButton
                    onClick={() => {
                      if (inputDisabled) return;
                      setFieldValue(
                        "monetaryDetails.hissaGeneratedTaxPaymentEnabled",
                        !isTaxPaymentLinkRequired
                      );
                    }}
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    value={isTaxPaymentLinkRequired || false}
                    label=""
                  />
                </div>
              </HStack>
              <p className="font-medium text-gray-400 text-xxs">
                Enabling this would generate a payment link for the employee
                through Juspay
              </p>
            </div>
            {!isTaxPaymentLinkRequired ? (
              <div className="flex-1">
                <Label className="mb-0 text-sm font-normal">
                  Tax Payment Link
                </Label>
                <Input
                  type="text"
                  disabled={inputDisabled}
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  name="monetaryDetails.taxPaymentLink"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder="Enter Payment Link"
                  value={taxPaymentLink}
                />
                {touched.monetaryDetails?.taxPaymentLink &&
                  errors.monetaryDetails?.taxPaymentLink && (
                    <Error text={errors.monetaryDetails.taxPaymentLink}></Error>
                  )}
              </div>
            ) : (
              <div className="flex-1">
                <Label className="mb-0 text-sm font-normal">
                  Tax Payment Profile
                </Label>
                <Select
                  onChange={(evt) => {
                    if (inputDisabled) return;
                    setFieldValue(
                      "monetaryDetails.paymentData.taxIdentifier",
                      evt.target.value
                    );
                  }}
                  disabled={inputDisabled}
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  value={values?.monetaryDetails?.paymentData?.taxIdentifier}
                  options={paymentProfiles || []}
                  textGetter={(e) => e.name}
                  valueGetter={(e) => e.id}
                />
              </div>
            )}
          </HStack>
          <VStack className="gap-8 px-6 pt-4">
            <div className="flex-1 pt-4">
              <HStack className="justify-between gap-2 ">
                <Label className="mb-0 text-sm font-normal">
                  Payment of Tax mandatory for Allocating Shares
                </Label>
                <div className="justify-end">
                  <SwitchButton
                    onClick={() => {
                      if (inputDisabled) return;
                      setFieldValue(
                        "monetaryDetails.taxMandatoryForCompletion",
                        !taxMandatoryForCompletion
                      );
                    }}
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    value={taxMandatoryForCompletion || false}
                    label=""
                  />
                </div>
              </HStack>
              <p className="font-medium text-gray-400 text-xxs">
                Enabling this will make it mandatory for the employee to pay tax
                before they can complete the exercise process.
              </p>
            </div>
          </VStack>
          <div className="flex-1 px-6 pt-8">
            <VStack className="flex-1">
              <VStack className="justify-between ">
                <VStack className=" ">
                  <Label className="text-sm font-normal">
                    Additional Payment Details
                  </Label>
                  <p className="font-medium text-gray-400 text-xxs pb-4">
                    Any additional information that you would like to provide
                    about the payment or tax process will be visible to
                    employee.
                  </p>
                </VStack>
                <TextArea
                  className={`min-h-32 ${
                    inputDisabled ? "cursor-not-allowed" : ""
                  }`}
                  disabled={inputDisabled}
                  name="taxDetailsInfoText"
                  onBlur={handleBlur}
                  placeholder="Write here..."
                  value={values.monetaryDetails.taxDetailsInfoText || ""}
                  onChange={(evt) => {
                    if (inputDisabled) return;
                    setFieldValue(
                      "monetaryDetails.taxDetailsInfoText",
                      evt.target.value
                    );
                  }}
                />
              </VStack>
            </VStack>
          </div>
        </div>
      )}
      <VStack className="justify-between pt-8 pb-2 pl-6 text-base font-medium border-b">
        <HStack className="justify-between">
          <div className="flex justify-between ">
            Email Notifications to Admins and Employees
          </div>
          <div className="justify-end pr-6">
            <SwitchButton
              onClick={() => {
                if (inputDisabled) return;
                setFieldValue(
                  "monetaryDetails.sendTransactionEmails",
                  !sendTransactionEmails
                );
              }}
              disabled={inputDisabled}
              className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
              value={sendTransactionEmails}
              label=""
            />
          </div>
        </HStack>
        <p className="font-medium text-gray-400 text-xxs">
          Enabling this will allow sending email notifications to admins and
          employees for various transaction events. Make sure to enable Hissa
          email toggle from settings
        </p>
      </VStack>
      <TemplateVariablesModal
        open={showVariablesModal}
        onClose={() => setShowVariablesModal(false)}
        templateType="EXERCISE_LETTER"
      />
      <Dialog
        maxWidth="sm"
        fullWidth
        open={showValidationDialog}
        onClose={() => setShowValidationDialog(false)}
      >
        <VStack className="bg-white rounded-lg overflow-hidden">
          <HStack className="items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
            <HStack className="items-center gap-2">
              <p className="text-base font-semibold text-gray-800">
                File Validation Results
              </p>
            </HStack>
            <button
              onClick={() => setShowValidationDialog(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <Icon icon="material-symbols:close" width={20} />
            </button>
          </HStack>
          <VStack className="gap-2 px-6 pt-4 pb-6">
            {validationResults.map((result, index) => (
              <HStack
                key={index}
                className={`items-start gap-3 p-3 rounded-md border ${
                  result.valid
                    ? "bg-green-50 border-green-200"
                    : "bg-red-50 border-red-200"
                }`}
              >
                <Icon
                  icon={result.valid ? "mdi:check-circle" : "mdi:close-circle"}
                  width={20}
                  height={20}
                  className={
                    result.valid
                      ? "text-green-500 mt-0.5 shrink-0"
                      : "text-red-500 mt-0.5 shrink-0"
                  }
                />
                <VStack className="gap-0.5">
                  <p className="text-sm font-medium text-gray-800">
                    {result.name}
                  </p>
                  {!result.valid && (
                    <p className="text-xs text-red-600">{result.reason}</p>
                  )}
                  {result.valid && (
                    <p className="text-xs text-green-600">Valid template</p>
                  )}
                </VStack>
              </HStack>
            ))}
          </VStack>
          <div className="flex justify-end px-6 pb-6">
            <ButtonPrimary onClick={() => setShowValidationDialog(false)}>
              Close
            </ButtonPrimary>
          </div>
        </VStack>
      </Dialog>
      <Dialog
        maxWidth="xl"
        open={showPreview}
        onClose={() => setShowPreview(false)}
      >
        <div className="mx-auto bg-white rounded-lg min-w-min min-h-min">
          <div className="font-semibold border-b p-7">
            <div className="flex items-center justify-between">
              <VStack className="gap-2 text-lg2 text-black-501">
                <p>Preview</p>
                <p className="text-sm4 text-gray-401">Email Template Preview</p>
              </VStack>
              <span
                className="cursor-pointer"
                onClick={() => setShowPreview(false)}
              >
                X
              </span>
            </div>
            <VStack className="w-full gap-4 p-7">
              <div dangerouslySetInnerHTML={{ __html: previewContent }} />
            </VStack>
          </div>
        </div>
      </Dialog>
      {sendTransactionEmails && (
        <VStack className="">
          <HStack className="gap-6 px-6 pt-4 border-b">
            <Box
              onClick={() => {
                setSelectedAdminEmails(!selectedAdminEmails);
              }}
              className={`text-xs3 font-semibold cursor-pointer py-1 ${
                selectedAdminEmails
                  ? "border-b-2 border-solid border-red-500 text-red-500"
                  : "text-gray-201 "
              } `}
            >
              <p>Emails to Admin</p>
            </Box>
            <Box
              onClick={() => {
                setSelectedAdminEmails(!selectedAdminEmails);
              }}
              className={`text-xs3 font-semibold cursor-pointer py-1 ${
                !selectedAdminEmails
                  ? "border-b-2 border-solid border-red-500 text-red-500"
                  : "text-gray-201 "
              } `}
            >
              <p>Emails to Employee</p>
            </Box>
          </HStack>
          {selectedAdminEmails && (
            <VStack className="px-6 py-2 ">
              <HStack>
                <div className="flex-1 pt-4">
                  <HStack className="justify-between gap-1 ">
                    <Label className="mb-0 text-sm font-normal">
                      Emails To Admin
                    </Label>
                    <div className="justify-end">
                      <SwitchButton
                        onClick={() => {
                          if (inputDisabled) return;
                          setFieldValue(
                            "monetaryDetails.sendTransactionEmailsToAdmin",
                            !values.monetaryDetails.sendTransactionEmailsToAdmin
                          );
                        }}
                        disabled={inputDisabled}
                        className={`${
                          inputDisabled ? "cursor-not-allowed" : ""
                        }`}
                        value={
                          values.monetaryDetails.sendTransactionEmailsToAdmin
                        }
                        label=""
                      />
                    </div>
                  </HStack>
                  <p className="font-medium text-gray-400 text-xxs">
                    Enabling this Will be sending emails to Admin
                  </p>
                </div>
              </HStack>
              {values.monetaryDetails.sendTransactionEmailsToAdmin &&
                values.monetaryDetails.defaultEmailTemplates
                  ?.filter((template) => template.to === "Admin") // Filter templates for admin
                  .map((template, index) => {
                    return (
                      <VStack key={index} className="py-2 pl-6">
                        <div className="flex-1 pt-4 mb-0">
                          <HStack className="justify-between gap-1">
                            <VStack className="gap-1">
                              <Label className="mb-0 text-sm font-normal cursor-pointer hover:underline">
                                {template.templateName}
                              </Label>
                              <p className="font-medium text-gray-400 text-xxs">
                                {template.description ||
                                  "No description available"}
                              </p>
                            </VStack>
                            <HStack className="items-center justify-end gap-6">
                              <HStack>
                                <ButtonPrimary1
                                  onClick={() =>
                                    handlePreview(template.templateLink)
                                  }
                                >
                                  Preview
                                </ButtonPrimary1>
                              </HStack>
                              <HStack>
                                <SwitchButton
                                  onClick={() => handleToggleEmail(template)} // Handle toggle logic
                                  className=""
                                  value={template.sendEmail}
                                  label=""
                                />
                              </HStack>
                            </HStack>
                          </HStack>
                        </div>
                      </VStack>
                    );
                  })}
            </VStack>
          )}

          {!selectedAdminEmails && (
            <VStack className="px-6 py-2 ">
              <HStack>
                <div className="flex-1 pt-4">
                  <HStack className="justify-between gap-2 ">
                    <Label className="mb-0 text-sm font-normal">
                      Emails To Employee
                    </Label>
                    <div className="justify-end">
                      <SwitchButton
                        onClick={() => {
                          if (inputDisabled) return;
                          setFieldValue(
                            "monetaryDetails.sendTransactionEmailsToEmployee",
                            !values.monetaryDetails
                              .sendTransactionEmailsToEmployee
                          );
                        }}
                        disabled={inputDisabled}
                        className={`${
                          inputDisabled ? "cursor-not-allowed" : ""
                        }`}
                        value={
                          values.monetaryDetails.sendTransactionEmailsToEmployee
                        }
                        label=""
                      />
                    </div>
                  </HStack>
                  <p className="font-medium text-gray-400 text-xxs">
                    Enabling this Will be sending emails to Employee
                  </p>
                </div>
              </HStack>
              {values.monetaryDetails.sendTransactionEmailsToEmployee &&
                values.monetaryDetails.defaultEmailTemplates
                  ?.filter((template) => template.to === "Employee") // Filter templates for admin
                  .map((template, index) => {
                    return (
                      <VStack key={index} className="py-2 pl-6">
                        <div className="flex-1 pt-4 mb-0">
                          <HStack className="justify-between gap-1">
                            <VStack className="gap-1">
                              <Label className="mb-0 text-sm font-normal cursor-pointer hover:underline">
                                {template.templateName}
                              </Label>
                              <p className="font-medium text-gray-400 text-xxs">
                                {template.description ||
                                  "No description available"}
                              </p>
                            </VStack>
                            <HStack className="items-center justify-end gap-6">
                              <HStack>
                                <ButtonPrimary1
                                  onClick={() =>
                                    handlePreview(template.templateLink)
                                  }
                                >
                                  Preview
                                </ButtonPrimary1>
                              </HStack>
                              <HStack>
                                <SwitchButton
                                  onClick={() => {
                                    if (inputDisabled) return;
                                    handleToggleEmail(template);
                                  }}
                                  disabled={inputDisabled}
                                  className={`${
                                    inputDisabled ? "cursor-not-allowed" : ""
                                  }`}
                                  value={template.sendEmail}
                                  label=""
                                />
                              </HStack>
                            </HStack>
                          </HStack>
                        </div>
                      </VStack>
                    );
                  })}
            </VStack>
          )}
        </VStack>
      )}
      <VStack className="justify-between pt-8 pb-2 text-base font-medium ">
        <div className="flex justify-between pb-2 pl-6 border-b">
          Transaction Settings
        </div>
        <div className="justify-between text-base font-medium">
          <VStack className="gap-8 px-6 ">
            <div className="flex-1 pt-4">
              <HStack className="justify-between gap-2 ">
                <Label className="mb-0 text-sm font-normal">
                  Auto Approve of Exercise Requests
                </Label>
                <div className="justify-end">
                  <SwitchButton
                    onClick={() => {
                      if (inputDisabled) return;
                      setFieldValue(
                        "monetaryDetails.autoApproveExerciseRequests",
                        !autoApproveExerciseRequests
                      );
                    }}
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    value={autoApproveExerciseRequests || false}
                    label=""
                  />
                </div>
              </HStack>
              <p className="font-medium text-gray-400 text-xxs">
                <p className="font-medium text-gray-400 text-xxs">
                  To use auto-approval, please enable the payment link first.
                  Enabling this will approve all exercise requests that come in.
                  Keep this switched off if you would like a decision-based
                  approval system.
                </p>
              </p>
            </div>
          </VStack>
          <VStack className="gap-8 px-6 pt-4">
            <div className="flex-1 pt-4">
              <HStack className="justify-between gap-2 ">
                <Label className="mb-0 text-sm font-normal">
                  Demat Account Details
                </Label>
                <div className="justify-end">
                  <SwitchButton
                    onClick={() => {
                      if (inputDisabled) return;
                      setFieldValue(
                        "monetaryDetails.isDematAccountMandatory",
                        !isDematAccountMandatory
                      );
                    }}
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    value={isDematAccountMandatory || false}
                    label=""
                  />
                </div>
              </HStack>
              <p className="font-medium text-gray-400 text-xxs">
                Enabling this will require employees to provide their demat
                account details before they can complete the exercise process.
              </p>
            </div>
            {isDematAccountMandatory && (
              <HStack className="gap-8">
                <div className="flex-1">
                  <Label className="mb-0 text-sm font-normal">
                    Allowed Depositories
                  </Label>
                  <MultiSelectV2
                    multiple={true}
                    placeholder=""
                    optionValues={allowedDematDepositories}
                    _className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    handleSelection={(vals) => {
                      if (inputDisabled) return;
                      setFieldValue(
                        "monetaryDetails.allowedDematDepositories",
                        vals
                      );
                    }}
                    options={[{ value: "CDSL" }, { value: "NSDL" }]}
                  />
                  {touched.monetaryDetails?.allowedDematDepositories &&
                    errors.monetaryDetails?.allowedDematDepositories && (
                      <Error
                        text={
                          errors.monetaryDetails
                            .allowedDematDepositories as string
                        }
                      ></Error>
                    )}
                </div>
                <div className="flex-1"></div>
              </HStack>
            )}
          </VStack>
          <VStack className="gap-8 px-6 pt-4">
            <div className="flex-1 pt-4">
              <HStack className="justify-between gap-2 ">
                <Label className="mb-0 text-sm font-normal">
                  Strictly Consider Company Currency
                </Label>
                <div className="justify-end">
                  <SwitchButton
                    onClick={() => {
                      if (inputDisabled) return;
                      setFieldValue(
                        "monetaryDetails.strictlyConsiderCompanyCurrency",
                        !strictlyConsiderCompanyCurrency
                      );
                    }}
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    value={strictlyConsiderCompanyCurrency || true}
                    label=""
                  />
                </div>
              </HStack>
              <p className="font-medium text-gray-400 text-xxs">
                {`Enabling this will ensure that all monetary values are
                consistently represented in the company's base currency,
                regardless of the Employee's location or currency preferences.`}
              </p>
            </div>
          </VStack>
          <VStack className="gap-8 px-6 pt-4">
            <div className="flex-1 pt-4">
              <HStack className="justify-between gap-2 ">
                <Label className="mb-0 text-sm font-normal">
                  Hide Tax Details from Employee
                </Label>
                <div className="justify-end">
                  <SwitchButton
                    onClick={() => {
                      if (inputDisabled) return;
                      setFieldValue(
                        "monetaryDetails.hideTaxDetails",
                        !hideTaxDetails
                      );
                    }}
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    value={hideTaxDetails || false}
                    label=""
                  />
                </div>
              </HStack>
              <p className="font-medium text-gray-400 text-xxs">
                {`Enabling this will ensure that all tax-related information is hidden from the Employee Portal`}
              </p>
            </div>
            <div className="flex-1 pt-8">
              <VStack className="flex-1">
                <VStack className="justify-between ">
                  <VStack className=" ">
                    <Label className="text-sm font-normal">
                      Additional Info to show for employees
                    </Label>
                    <p className="font-medium text-gray-400 text-xxs pb-4">
                      Any additional information that you would like to provide
                      about the exercise flow will be visible to employee in the
                      final step of exercise.
                    </p>
                  </VStack>
                  <TextArea
                    className={`min-h-32 ${
                      inputDisabled ? "cursor-not-allowed" : ""
                    }`}
                    disabled={inputDisabled}
                    name="additionalPaymentInfoText"
                    onBlur={handleBlur}
                    placeholder="Write here..."
                    value={
                      values.monetaryDetails.additionalPaymentInfoText || ""
                    }
                    onChange={(evt) => {
                      if (inputDisabled) return;
                      setFieldValue(
                        "monetaryDetails.additionalPaymentInfoText",
                        evt.target.value
                      );
                    }}
                  />
                </VStack>
              </VStack>
            </div>
          </VStack>
        </div>
      </VStack>
      <HStack className="justify-between px-6 pt-8">
        <ButtonPrimary1
          onClick={() => {
            props.onBackClick();
          }}
        >
          Back
        </ButtonPrimary1>
        <ButtonPrimary
          onClick={() => {
            handleSubmit();
          }}
        >
          Next
        </ButtonPrimary>
      </HStack>
    </VStack>
  );
};

export default ExerciseDetails;
